// =============================================================================
//  iConnect — Mega menu (desktop) + offcanvas (mobile)   (inc/mega-menu.php)
//
//  Needs GSAP. Works alongside the header hide/show script: the mega menu is
//  placed under the header's live position on open and closes once the page
//  scrolls, so it never floats detached from a hidden header.
// =============================================================================

;(function initMegaMenu() {
  const header = document.getElementById('wrapper-navbar');
  const mega = document.getElementById('iconnect-mega');
  if (!header || !mega) return;

  const OPEN_DELAY = 120;   // hover before the first panel opens
  const SWITCH_DELAY = 80;  // hover before switching panels (forgives diagonal moves)
  const CLOSE_DELAY = 250;  // grace period to travel from trigger to panel
  const SCROLL_CLOSE = 40;  // px of scroll that closes an open panel
  const GUTTER = 16;

  const speed = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1;

  // Pair each top-level item with its panel by menu item ID.
  const items = [...header.querySelectorAll('#main-menu > .menu-item-has-children > a')].map((trigger) => {
    const id = trigger.parentElement.id.replace('menu-item-', '');
    const panel = document.getElementById(`iconnect-mega-${id}`);
    if (!panel) throw new Error(`Mega menu: no panel for #menu-item-${id}`);

    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', panel.id);

    return { trigger, panel, links: [...panel.querySelectorAll('a')] };
  });

  let active = null;
  let openedByHover = false;
  let openScrollY = 0;
  let timer;

  // Where the shell sits for an item: under the header, centred on the trigger, kept on screen.
  function geometry(item) {
    const t = item.trigger.getBoundingClientRect();
    const width = item.panel.offsetWidth;
    const maxX = document.documentElement.clientWidth - width - GUTTER;

    return {
      top: header.getBoundingClientRect().bottom,
      x: Math.min(Math.max(t.left + t.width / 2 - width / 2, GUTTER), maxX),
      width,
      height: item.panel.offsetHeight,
    };
  }

  function setExpanded(item, expanded) {
    item.trigger.setAttribute('aria-expanded', String(expanded));
  }

  function open(item, viaHover) {
    clearTimeout(timer);
    openedByHover = viaHover;
    if (item === active) return;

    const previous = active;
    active = item;
    setExpanded(item, true);

    if (previous) {
      // Switch: morph the shell, slide panels in the direction of travel.
      setExpanded(previous, false);
      const dir = items.indexOf(item) > items.indexOf(previous) ? 1 : -1;

      gsap.to(mega, { ...geometry(item), duration: 0.45 * speed, ease: 'power3.out', overwrite: 'auto' });
      gsap.to(previous.panel, { autoAlpha: 0, x: -40 * dir, duration: 0.2 * speed, overwrite: 'auto' });
      gsap.fromTo(item.panel, { autoAlpha: 0, x: 40 * dir }, { autoAlpha: 1, x: 0, duration: 0.4 * speed, delay: 0.08 * speed, ease: 'power2.out', overwrite: 'auto' });
    } else {
      // First open: drop down from behind the header.
      openScrollY = window.scrollY;
      gsap.set(items.map((i) => i.panel), { autoAlpha: 0, overwrite: true });
      gsap.set(item.panel, { autoAlpha: 1, x: 0 });
      gsap.set(mega, { ...geometry(item), visibility: 'visible', overwrite: true }); // visible now so links can take focus
      gsap.fromTo(mega, { opacity: 0, y: -12 }, { opacity: 1, y: 0, duration: 0.35 * speed, ease: 'power2.out', overwrite: 'auto' });
    }

    gsap.fromTo(item.links, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3 * speed, stagger: 0.025 * speed, delay: 0.1 * speed, ease: 'power2.out', overwrite: 'auto' });
  }

  function close() {
    clearTimeout(timer);
    if (!active) return;

    const closing = active;
    active = null;
    setExpanded(closing, false);

    gsap.to(mega, {
      autoAlpha: 0,
      y: -12,
      duration: 0.25 * speed,
      ease: 'power2.in',
      overwrite: 'auto',
      onComplete: () => gsap.set(closing.panel, { autoAlpha: 0 }),
    });
  }

  function later(fn, delay) {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  }

  // Next focus stop after a panel: the following top-level link.
  function linkAfter(item) {
    return item.trigger.parentElement.nextElementSibling?.querySelector('a');
  }

  // ── Mouse: hover intent ──
  items.forEach((item) => {
    const li = item.trigger.parentElement;

    li.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      later(() => open(item, true), active ? SWITCH_DELAY : OPEN_DELAY);
    });

    li.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      later(close, CLOSE_DELAY);
    });

    // Click / tap / Enter toggles. A click on a hover-opened panel keeps it open.
    item.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (active === item && !openedByHover) close();
      else open(item, false);
    });

    item.trigger.addEventListener('keydown', (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        item.trigger.click();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        open(item, false);
        item.links[0].focus();
      }
    });
  });

  mega.addEventListener('pointerenter', () => clearTimeout(timer));
  mega.addEventListener('pointerleave', (e) => {
    if (e.pointerType === 'mouse') later(close, CLOSE_DELAY);
  });

  // ── Keyboard: the panel lives after the header in the DOM, so stitch it
  //    into the tab order between its trigger and the next top-level link ──
  document.addEventListener('keydown', (e) => {
    if (!active) return;

    if (e.key === 'Escape') {
      const refocus = mega.contains(document.activeElement);
      const { trigger } = active;
      close();
      if (refocus) trigger.focus();
      return;
    }

    if (e.key !== 'Tab') return;

    const { trigger, links } = active;
    const first = links[0];
    const last = links[links.length - 1];
    let target = null;

    if (!e.shiftKey && document.activeElement === trigger) target = first;
    else if (e.shiftKey && document.activeElement === first) target = trigger;
    else if (!e.shiftKey && document.activeElement === last) target = linkAfter(active);

    if (target) {
      e.preventDefault();
      target.focus();
    }
  });

  // Close when keyboard focus leaves the trigger + panel pair.
  document.addEventListener('focusin', (e) => {
    if (active && e.target !== active.trigger && !mega.contains(e.target)) close();
  });

  // Close on a click/tap anywhere outside the triggers and panel.
  document.addEventListener('pointerdown', (e) => {
    if (!active || mega.contains(e.target)) return;
    if (!items.some((item) => item.trigger.parentElement.contains(e.target))) close();
  });

  // Close once the page actually scrolls — the header script is about to move the header.
  window.addEventListener('scroll', () => {
    if (active && Math.abs(window.scrollY - openScrollY) > SCROLL_CLOSE) close();
  }, { passive: true });

  window.addEventListener('resize', close);
})();


;(function initOffcanvas() {
  const dialog = document.getElementById('iconnect-offcanvas');
  if (!dialog) return;

  const toggles = document.querySelectorAll('[aria-controls="iconnect-offcanvas"]');

  toggles.forEach((toggle) => toggle.addEventListener('click', () => {
    dialog.showModal();
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
  }));

  dialog.addEventListener('close', () => {
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  });

  dialog.querySelector('[data-offcanvas-close]').addEventListener('click', () => dialog.close());

  // The backdrop is part of the dialog element; a click straight on it closes.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  // Close when rotating/resizing into the desktop layout.
  matchMedia('(min-width: 992px)').addEventListener('change', (e) => {
    if (e.matches) dialog.close();
  });
})();
